/*
 * THE ARRIVAL: a flight in, and one name at the end of it.
 *
 * A reader opening this map for the first time is not handed 1,488 figures and a search box. The
 * field arrives at full extent and holds, the camera flies over the centre, the rim and the three
 * busiest neighbourhoods with a line said over each, and then it descends onto one name — which is
 * where they are left standing, with the dark closing in around them.
 *
 * IT USED TO RUN THE OTHER WAY. The design opens with six names walked one at a time and only then
 * the field pulling back, and that was built and then flipped: the payoff arrived ninety seconds
 * after a reader who did not yet know there was one, and the six steps read as a gate. Flipping it
 * costs nothing the walk was for. The fog still closes in as the camera lands, so "the rest stays
 * dark until you go there" is now something the reader watches happen to a field they have just
 * been shown — a stronger version of the same claim than a promise about names they never saw.
 *
 * The phases live here, as data and pure functions, because "where is this reader in the arrival"
 * is a fact that several pieces of chrome have to agree on, and a phase machine spread across
 * their effects is several machines that will eventually disagree.
 */

/**
 * Where a reader is in the arrival. `map` is the end of it, and every later visit.
 *
 * THERE WAS A `landing` BETWEEN THEM, and it is gone as of 2026-08-22 on Ariel's instruction:
 * *"drop the feature that prevents you from going to a node"*, and then, when the first search for
 * it turned up nothing, *"there is a feature that prevents opening, it requires reading linked
 * nodes"*.
 *
 * That was this phase. It held the reader on `Ground.tsx`, whose whole design was that the way
 * onward is a name tapped INSIDE the verse — its own note said the chips beside it were "the
 * FALLBACK, not the path", shown only where the passage cited nobody, because offering them always
 * "would teach a reader to work the row and never read the passage". Which is exactly the
 * requirement that has now been dropped: a reader may open any name on the map, and does not have
 * to read their way to it.
 *
 * The flight is untouched. It still comes down onto a figure and the dark still closes around it;
 * what changed is that the reader is then on the map rather than on a screen with one way out.
 */
export type Phase = 'tour' | 'map';

/**
 * How many names a returning reader must already have opened to skip the arrival.
 *
 * ONE. The flight is a thing you watch, not a thing you complete, so having watched it is what
 * decides — and the only durable evidence of that is having ended up standing on the name it lands
 * on. A reader who opened even one figure has been here; a reader who has opened none has not.
 *
 * (It was six, when the arrival was six names walked. That gate is what this replaced.)
 */
export const ARRIVED_AFTER = 1;

export interface Walk {
  phase: Phase;
}

/**
 * Where to put a reader when the map opens.
 *
 * `opened` is how many figures they have ever opened, from the trail — the only record that
 * survives closing the tab, and derived rather than counted separately so a second tally cannot
 * drift from it.
 */
export function startWalk(opened: number, asked = false): Walk {
  /*
   * A READER WHO ASKED FOR SOMETHING IS NOT ARRIVING, THEY ARE GOING SOMEWHERE.
   *
   * `asked` is true when the URL names a figure, a passage, a comparison or a panel — which is how
   * every reader who follows a link from the static pages gets here, and every one of them has an
   * empty trail. Without this the arrival intercepted them: pressing "Open Abraham on the map" on a
   * search result opened a flight over an empty sky with a Skip button and no Abraham. Measured on
   * 2026-08-21, across 2,688 pages carrying 52,612 such links.
   *
   * The tour still runs for a first visit that asked for nothing, which is the visit it was written
   * for — somebody who typed the address, with no idea yet what any of this is.
   */
  if (asked) return { phase: 'map' };
  return { phase: opened >= ARRIVED_AFTER ? 'map' : 'tour' };
}

/** The order the arrival runs in. `map` has no next — it is where a reader is left. */
const AFTER: Record<Phase, Phase> = {
  tour: 'map',
  map: 'map',
};

/** Move on. Each phase decides for itself WHEN — the flight ends, or the reader presses on. */
export function advance(walk: Walk): Walk {
  return { phase: AFTER[walk.phase] };
}

/**
 * Cut the arrival short and go straight to the map.
 *
 * There has to be a way out, from the first second. A reader who has met this before, or who
 * simply wants the field, must not be held in a flight they did not ask for — an arrival with no
 * exit is a modal dialogue with better typography.
 */
export function skipWalk(): Walk {
  return { phase: 'map' };
}

/** Whether the map's own chrome should be showing yet. */
export function mapIsOpen(walk: Walk): boolean {
  return walk.phase === 'map';
}
